import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "flex flex-col justify-between px-[34px]" }
const _hoisted_2 = { class: "flex flex-col items-center justify-center mb-8" }
const _hoisted_3 = { class: "font-sourcesanspro-semibold text-lg2" }
const _hoisted_4 = { class: "font-sourcesanspro-regular text-base2 text-white/50" }
const _hoisted_5 = { class: "flex flex-col w-full align-center" }
const _hoisted_6 = {
  key: 0,
  class: "flex flex-col w-full align-center"
}
const _hoisted_7 = { class: "flex px-6 py-4 items-center text-white" }
const _hoisted_8 = { class: "font-sourcesanspro-semibold text-l2" }
const _hoisted_9 = { class: "border-y border-y-white/5 px-6" }
const _hoisted_10 = ["validation-schema"]
const _hoisted_11 = {
  key: 2,
  class: "mb-2"
}
const _hoisted_12 = { class: "font-sourcesanspro-regular text-sm2 text-center text-white/50" }
const _hoisted_13 = { class: "mb-8" }
const _hoisted_14 = {
  key: 0,
  class: "flex w-full items-center justify-between"
}
const _hoisted_15 = {
  key: 0,
  class: "text-m2"
}
const _hoisted_16 = {
  key: 1,
  class: "text-m2"
}
const _hoisted_17 = { key: 1 }

import vue, { ref, onMounted } from 'vue';
import router from '@/router';
import { useRoute } from 'vue-router';
import { IonButton, IonContent, IonList } from '@ionic/vue';
import { useI18n } from 'vue-i18n';

import { Address } from '@/shared/interfaces/wallet';
import { TransactionItem } from '@/shared/interfaces/transaction';
import { TransactionType } from '@/enums/wallet.enum';
import { useCheckout } from '@/shared/composables/useCheckout';
import hexRgb from 'hex-rgb';
import ItemsDetails from '@/shared/components/ui/ItemsDetails.vue';
import rgbHex from 'rgb-hex';
import TextInput from '@/shared/components/sign-up-forms/TextInput.vue';
import TopBar from '@/shared/components/TopBar.vue';
import useUserProfile from '@/modules/profile/composables/useUserProfile';
import useUtils from '@/shared/composables/useUtils';
import useWallet from '@/modules/wallet/composables/useWallet';
import { useAuthStore } from '@/stores/auth/authStore';
import { Wallet } from '@/shared/interfaces/balance';
import { Product } from '@/shared/interfaces/tableSpot';
import { useTransactionStore } from '@/stores/transaction/transactionStore';
import useTenant from '@/shared/composables/useTenant';
import useCurrencies from '@/shared/composables/useCurrencies';
import { useSharedStore } from '@/stores/shared/sharedStore';
import BackButton from '@/shared/components/BackButton.vue';

// Init

export default /*@__PURE__*/_defineComponent({
  __name: 'CheckoutPage',
  emits: ['onFormValidation'],
  setup(__props, { emit: __emit }) {

const { t } = useI18n();
const { getTenantConfigAndStyles } = useTenant();
const { tenantConfig } = getTenantConfigAndStyles();
const emit = __emit;
const { getUserProfile } = useUserProfile();
const { userProfile } = getUserProfile();
const route = useRoute();
const transactionType = route.query.transactionType;
const pendingTopUp = route.query.pendingTopUp;
const checkStock = transactionType === TransactionType.SELF_ORDER ? true : false;
const { calculateShade } = useUtils();
const primary_color = window.primary_color;
const primaryRgb = hexRgb(window.primary_color, { format: 'array' });
const primaryShade = '#' + rgbHex(calculateShade(primaryRgb.toString(), 0.2));
const buttonText = ref('');
const buttonDisabled = ref(true);
const formData: any = {
	quantity: 0,
	total: [],
};
const totalCurrenciesNotEnough: any = ref([]);
const totalItemsPriceString = ref(' ');
const totalPriceInEur = ref('');
const transactionStore = useTransactionStore();
const authStore = useAuthStore();
const sharedStore = useSharedStore();
const { fiatCurrency } = useCurrencies();
const requiresLogin = ref(false);

// Check user's wallet balance
const { getWallets } = useWallet();
const { refetchWalletsData, wallets } = getWallets();

const { items, totalItemCount, handleCheckout, handleCheckoutWithForm, totalItemsPrice, getTotalPricePerCurrency, billingAddressValidationSchema } = useCheckout();

const address: Address = {
	country: userProfile.value?.country ?? 'Belgium',
	city: userProfile.value?.city ?? '',
	address_street: userProfile.value?.address_street ?? '',
	address_number: userProfile.value?.address_number ?? '',
	street: userProfile.value?.address_street ?? '',
	number: userProfile.value?.address_number ?? '',
	postal_code: userProfile.value?.postal_code ?? '',
};

// Go back
const goToLogin = () => {
	console.log('[CheckoutPage] Navigating to login. Saving redirect:', route.fullPath);
	sharedStore.pendingCheckoutRedirect = route.fullPath;
	router.push('/auth/signinhome');
};
const goBack = () => {
	if (pendingTopUp) {
		router.push('/dashboard');
	} else {
		router.back();
	}
};

// Validating form
const onInputChange = (formValid: vue.ComputedRef<boolean>) => {
	emit('onFormValidation', formValid);
};

onMounted(async () => {
	const { isAuthenticated } = useAuthStore();

	if (isAuthenticated()) {
		await refetchWalletsData();
	}

	const totalPricePerCurrency = getTotalPricePerCurrency();
	if (isAuthenticated()) {
		if (wallets && wallets.value) {
			const totalCurrenciesNotEnough: any = [];

			// Handle Self Order Products
			if (transactionType === TransactionType.SELF_ORDER) {
				const filteredProducts = items.filter((item) => item.additionalInfo.id !== 'administrative_fee');
				filteredProducts.forEach((item) => {
					const product = new Product(item.additionalInfo.product);

					// Check if the product has only one price
					// and user has a wallet for that currency
					const wallet = wallets.value?.find((wallet) => wallet.currency.id === product.currency.id);
					const isOnePrice = product.prices.length === 1;
					if (wallet && isOnePrice) {
						// Check if user has enough balance for that product
						// If not, disable the button and show a message
						const subtotal = item.unitPrice! * item.quantity;
						const totalPriceForCurrency = totalPricePerCurrency.find((currency: any) => currency.currencyId === product.currency.id);

						if (totalPriceForCurrency && totalPriceForCurrency.total > +wallet.current_balance && product.currency.topupable) {
							// Set the administrative fee as this is require a top up
							// This will be added just once
							transactionStore.setAdministrativeFee(+tenantConfig.value!.top_up_cost, t('transaction.administrative_fee'));

							const currency = totalCurrenciesNotEnough.find((currency: any) => currency.wallet.currency.id === wallet.currency.id);
							if (!currency) {
								// If wallet currency is the fiat currency, we add the administrative fee to the total
								const calculatedSubtotal = wallet.currency.name === fiatCurrency.value.nameId ? subtotal + +tenantConfig.value!.top_up_cost : subtotal;

								totalCurrenciesNotEnough.push({
									subtotal: calculatedSubtotal,
									wallet: wallet,
								});
							} else {
								currency.subtotal += subtotal;
							}
							buttonDisabled.value = true;
						} else {
							// If user has enough balance, but as it is only one currency, we left the user to top up and pay
							// Set the administrative fee as this is require a top up
							// transactionStore.setAdministrativeFee(+tenantConfig.value!.top_up_cost, t('transaction.administrative_fee'));
						}
					} else if (wallet && !isOnePrice) {
						// Product has multiple prices: We left backend to resolve this
						console.log('Wallet was found but product has multiple prices');
					} else {
						// Wallet was not found
						console.log('Wallet not found');
					}
				});
			}
			if (transactionType === TransactionType.PAYMENT || transactionType === 'QUEUE_BOOKING' || transactionType === 'QUEUE_UPSELL') {
				totalPricePerCurrency.forEach((currencyTotal) => {
					// Check if user has a wallet for that currency
					const wallet = wallets.value?.find((wallet) => wallet.currency.id === currencyTotal.currencyId);

					if (wallet) {
						// Check if user has enough balance for that product
						// If not, disable the button and show a message
						if (currencyTotal.total > +wallet.current_balance && wallet.currency.topupable) {
							// Set the administrative fee as this is require a top up
							// This will be added just once
							transactionStore.setAdministrativeFee(+tenantConfig.value!.top_up_cost, t('transaction.administrative_fee'));

							const currencyNotEnough = totalCurrenciesNotEnough.find((currency: any) => currency.wallet.currency.id === wallet.currency.id);
							if (!currencyNotEnough) {
								const calculatedSubtotal = currencyTotal.total;

								totalCurrenciesNotEnough.push({
									subtotal: calculatedSubtotal,
									wallet: wallet,
								});
							} else {
								currencyNotEnough.subtotal += currencyTotal.total;
							}
							buttonDisabled.value = true;
						} else {
							// If user has enough balance, but as it is only one currency, we left the user to top up and pay
							// Set the administrative fee as this is require a top up
							// transactionStore.setAdministrativeFee(+tenantConfig.value!.top_up_cost, t('transaction.administrative_fee'));
						}
					} else {
						// Wallet was not found
						console.log('Wallet not found');
					}
				});
			}

			if (totalCurrenciesNotEnough.length === 1 && totalCurrenciesNotEnough[0].wallet.currency.topupable) {
				formData.quantity = totalPricePerCurrency.length;
				formData.total = totalPricePerCurrency.map((currency) => {
					return {
						currency_id: currency.currencyId,
						amount: currency.total,
					};
				});
				const currency = totalCurrenciesNotEnough[0];
				const totalToTopUp = Math.abs(+currency.wallet.current_balance - currency.subtotal);

				buttonText.value = t('checkout.pay_now');

				buttonDisabled.value = false;
				if (totalToTopUp > 0) {
					const currency = totalCurrenciesNotEnough[0].wallet.currency;
					if (currency.name === fiatCurrency.value.nameId) {
						formData.top_up_amount = totalToTopUp.toFixed(2);
					} else {
						formData.total = formData.total.filter((item: any) => item.currency_id);
						formData.top_up_amount = totalToTopUp.toFixed(2);
					}
				}
				if (+formData.top_up_amount < 1) {
					buttonText.value = t('top_up.minimum_top_up_amount'); // Inform the user
					buttonDisabled.value = true;
				}
			} else if (totalCurrenciesNotEnough.length > 1) {
				buttonText.value = t('error.insufficient_balance'); // Inform the user
				buttonDisabled.value = true;
			} else {
				formData.quantity = totalPricePerCurrency.length;
				formData.total = totalPricePerCurrency.map((currency) => {
					return {
						currency_id: currency.currencyId,
						amount: currency.total,
					};
				});
				buttonText.value = t('checkout.pay_now');
				buttonDisabled.value = false;
			}
		}
	}
	if (!isAuthenticated()) {
		const isProfileMode = (tenantConfig.value?.enable_public_mode ?? false) === false && (tenantConfig.value?.enable_full_guest_mode ?? false) === false;
		const isSelfOrder = transactionType === TransactionType.SELF_ORDER;
		console.log('[CheckoutPage] User not authenticated');
		console.log('[CheckoutPage] isProfileMode:', isProfileMode, '| enable_public_mode:', tenantConfig.value?.enable_public_mode, '| enable_full_guest_mode:', tenantConfig.value?.enable_full_guest_mode);
		console.log('[CheckoutPage] isSelfOrder:', isSelfOrder, '| transactionType:', transactionType);

		if (isProfileMode && isSelfOrder) {
			console.log('[CheckoutPage] PROFILE MODE + SELF ORDER -> Login required before payment');
			buttonText.value = t('checkout.login_and_pay');
			buttonDisabled.value = false;
			requiresLogin.value = true;
		} else {
			console.log('[CheckoutPage] PUBLIC/GUEST MODE -> Proceed as guest');
			formData.quantity = totalPricePerCurrency.length;
			formData.total = totalPricePerCurrency.map((currency) => {
				formData.top_up_amount = currency.total;
				return {
					currency_id: currency.currencyId,
					amount: currency.total,
				};
			});
			buttonText.value = t('checkout.pay_now');
			buttonDisabled.value = false;
		}
	}
	totalItemsPriceString.value = transactionStore.calculateTotalsPerProductCurrency();
	totalPriceInEur.value = transactionStore.calculateTotalInEur();
	transactionStore.totalPriceString = totalItemsPriceString.value;
});

return (_ctx: any,_cache: any) => {
  const _component_ion_spinner = _resolveComponent("ion-spinner")!

  return (_openBlock(), _createBlock(_unref(IonContent), {
    style: _normalizeStyle(`background: linear-gradient(${_unref(primary_color)}, ${primaryShade});`)
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", null, [
          _createVNode(TopBar, null, {
            left: _withCtx(() => [
              _createVNode(BackButton, { onClick: goBack })
            ]),
            center: _withCtx(() => [...(_cache[2] || (_cache[2] = [
              _createElementVNode("div", { class: "flex grow items-center justify-center w-full" }, [
                _createElementVNode("div", { class: "mr-2" }, [
                  _createElementVNode("span", { class: "font-sourcesanspro-semibold text-l2" })
                ])
              ], -1)
            ]))]),
            _: 1
          }),
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("h1", _hoisted_3, _toDisplayString(_unref(t)('checkout.title')), 1),
            _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(items)?.length > 0 ? (_unref(items)?.length > 1 ? `${Number(_unref(totalItemCount))} ${_unref(t)('checkout.items')}` : `${Number(_unref(totalItemCount))} ${_unref(t)('checkout.item')}`) : _unref(t)('checkout.no_item')), 1)
          ]),
          _createElementVNode("div", _hoisted_5, [
            (_unref(items)?.length > 0)
              ? (_openBlock(), _createBlock(ItemsDetails, {
                  key: 0,
                  "border-class": "border-y border-y-white/5",
                  class: "backdrop-blur-xl bg-anykrowd-gray/20 text-white rounded-xl mb-4",
                  "text-class": "text-white",
                  "text-class-two": "text-white/70",
                  title: _unref(t)('checkout.order_details'),
                  items: 
							_unref(items).map((item) => {
								return {
									discount: item.discount,
									itemId: item.itemId,
									title: item.description,
									hideQuantity: false,
									hidePrice: false,
									price: item.unitPrice,
									quantity: item.quantity,
									totalPrice: item.itemPrice,
									totalPriceCurrency: item.additionalInfo?.product?.currency?.prefix,
								};
							})
						,
                  "total-title": _unref(items)?.length > 0 ? Number(_unref(totalItemCount)) + ` ${_unref(t)('checkout.items_total')}` : _unref(t)('checkout.empty'),
                  "total-price-string": totalItemsPriceString.value || '0',
                  "total-price": _unref(totalItemsPrice),
                  "total-price-in-eur": totalPriceInEur.value
                }, null, 8, ["title", "items", "total-title", "total-price-string", "total-price", "total-price-in-eur"]))
              : _createCommentVNode("", true)
          ]),
          (_unref(authStore).isAuthenticated())
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                (_unref(wallets) && _unref(wallets).length > 0)
                  ? (_openBlock(), _createBlock(ItemsDetails, {
                      key: 0,
                      "border-class": "border-y border-y-white/5",
                      class: "backdrop-blur-xl bg-anykrowd-gray/20 text-white rounded-xl mb-4",
                      "text-class": "text-white",
                      "text-class-two": "text-white/70",
                      title: _unref(t)('wallet.checkout.wallets_title'),
                      items: 
							_unref(wallets).map((wallet) => {
								return {
									itemId: wallet.id,
									title: wallet.currency.name,
									hideQuantity: true,
									quantity: 1,
									totalPrice: +wallet.current_balance,
									totalPriceCurrency: wallet.currency.prefix,
								};
							})
						,
                      "show-totals": false
                    }, null, 8, ["title", "items"]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          (_unref(transactionType) === _unref(TransactionType).SHOP_ORDER)
            ? (_openBlock(), _createBlock(_unref(IonList), {
                key: 1,
                class: "backdrop-blur-xl bg-anykrowd-gray/20 text-white rounded-xl mb-4"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("span", _hoisted_8, _toDisplayString(_unref(t)('checkout.billing_address.title')), 1)
                  ]),
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("form", {
                      id: "billingAddressForm",
                      onSubmit: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(handleCheckoutWithForm) && _unref(handleCheckoutWithForm)(...args))),
                      "validation-schema": _unref(billingAddressValidationSchema)
                    }, [
                      _createVNode(TextInput, {
                        "wrapper-class": "flex flex-wrap justify-left item-center my-2 border border-solid focus-within:background-white-15",
                        name: "country",
                        type: "text",
                        value: address.country,
                        placeholder: _ctx.$t('checkout.billing_address.country'),
                        onInputChanged: onInputChange
                      }, null, 8, ["value", "placeholder"]),
                      _createVNode(TextInput, {
                        "wrapper-class": "flex flex-wrap justify-left item-center my-2 border border-solid focus-within:background-white-15",
                        name: "city",
                        type: "text",
                        value: address.city,
                        placeholder: _ctx.$t('checkout.billing_address.city'),
                        onInputChanged: onInputChange
                      }, null, 8, ["value", "placeholder"]),
                      _createVNode(TextInput, {
                        "wrapper-class": "flex flex-wrap justify-left item-center my-2 border border-solid focus-within:background-white-15",
                        name: "address_street",
                        type: "text",
                        value: address.address_street,
                        placeholder: _ctx.$t('checkout.billing_address.street'),
                        onInputChanged: onInputChange
                      }, null, 8, ["value", "placeholder"]),
                      _createVNode(TextInput, {
                        "wrapper-class": "flex flex-wrap col-md-6 justify-left item-center my-2 border border-solid focus-within:background-white-15",
                        name: "address_number",
                        type: "text",
                        value: address.address_number,
                        placeholder: _ctx.$t('checkout.billing_address.address_number'),
                        onInputChanged: onInputChange
                      }, null, 8, ["value", "placeholder"]),
                      _createVNode(TextInput, {
                        "wrapper-class": "flex flex-wrap col-md-6 justify-left item-center mb-3 mt-2 border border-solid focus-within:background-white-15",
                        name: "postal_code",
                        type: "text",
                        value: address.postal_code,
                        placeholder: _ctx.$t('checkout.billing_address.postal_code'),
                        onInputChanged: onInputChange
                      }, null, 8, ["value", "placeholder"])
                    ], 40, _hoisted_10)
                  ])
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (_unref(transactionType) === _unref(TransactionType).PAYMENT)
            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                _createElementVNode("p", _hoisted_12, _toDisplayString(_unref(t)('checkout.check')), 1)
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_13, [
          (_unref(transactionType) === _unref(TransactionType).SHOP_ORDER)
            ? (_openBlock(), _createBlock(_unref(IonButton), {
                key: 0,
                disabled: _unref(totalItemsPrice) === 0,
                class: "w-full",
                expand: "full",
                form: "billingAddressForm",
                size: "large",
                shape: "round",
                type: "submit"
              }, {
                default: _withCtx(() => [
                  (_ctx.userWallet)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                        (+_unref(totalItemsPrice) > +_ctx.userWallet?.current_balance)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                              _createTextVNode(_toDisplayString(_unref(t)('checkout.top_up')) + " ", 1),
                              _createElementVNode("span", null, _toDisplayString(`€${(+_unref(totalItemsPrice) - +_ctx.userWallet?.current_balance).toFixed(2)}`), 1),
                              _createTextVNode(" & " + _toDisplayString(_unref(t)('checkout.pay')), 1)
                            ]))
                          : (_openBlock(), _createElementBlock("span", _hoisted_16, _toDisplayString(_unref(t)('checkout.pay_now')), 1)),
                        _createElementVNode("div", null, [
                          _createElementVNode("span", null, "€" + _toDisplayString((+_unref(totalItemsPrice)).toFixed(2) || 0), 1)
                        ])
                      ]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }, 8, ["disabled"]))
            : _createCommentVNode("", true),
          (_unref(transactionType) !== _unref(TransactionType).SHOP_ORDER)
            ? (_openBlock(), _createBlock(_unref(IonButton), {
                key: 1,
                disabled: buttonText.value === '' || totalCurrenciesNotEnough.value.length > 1 || buttonDisabled.value,
                class: "w-full hover:rounded-full hover:bg-white",
                expand: "full",
                size: "large",
                shape: "round",
                onClick: _cache[1] || (_cache[1] = ($event: any) => (requiresLogin.value ? goToLogin() : _unref(handleCheckout)(_unref(checkStock), formData)))
              }, {
                default: _withCtx(() => [
                  (buttonText.value === '')
                    ? (_openBlock(), _createBlock(_component_ion_spinner, {
                        key: 0,
                        name: "dots"
                      }))
                    : (_openBlock(), _createElementBlock("div", _hoisted_17, _toDisplayString(buttonText.value), 1))
                ]),
                _: 1
              }, 8, ["disabled"]))
            : _createCommentVNode("", true)
        ])
      ])
    ]),
    _: 1
  }, 8, ["style"]))
}
}

})